import {TFunction} from 'i18next';
import moment from 'moment';
import {Moment} from 'moment';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {HealthMeasurementType} from '../../../../../../../../backend/enums/health-measurement-type';
import {ActivityType} from '../../../../../../../../backend/interfaces/activity-type';
import {HealthMeasurement} from '../../../../../../../../backend/interfaces/health-measurement';
import {
  DateChartValuesProp,
  DateChartDomainProp,
} from '../../../../../../../../components/charts/date-chart/date-chart';
import {useSnackBar} from '../../../../../../../../contexts/snack-bar/context';
import {translations} from '../../../../../../../../i18n/translations';
import {EntityActivitiesRepository} from '../../../../../../../../repositories/entity-activities';
import {UsersRepository} from '../../../../../../../../repositories/users';
import {
  CLINICAL_SUPPORT_SECTOR_ID,
  CLINICAL_SUPPORT__VITAL_SIGNS_ID,
} from '../../../../../../../../utils/constants';

export type HealthFetchData = {
  fetching: boolean;
  items: HealthItem[];
  refresh: () => void;
};

export function useFetchUserHealthData(opts: {
  userId: string;
  startDate: Moment;
  endDate: Moment;
}): HealthFetchData {
  const [items, setItems] = useState<HealthItem[]>([]);
  const [fetching, setFetching] = useState(true);
  const snackBar = useSnackBar();
  const {t} = useTranslation();
  const abort = useRef<AbortController | undefined>();
  const timeout = useRef<NodeJS.Timeout | undefined>();
  const {entityId}: {entityId: string} = useParams();

  const refresh = useCallback(() => {
    abort.current?.abort();
    abort.current = new AbortController();

    const fetch = async (start: Moment, end: Moment): Promise<void> => {
      try {
        const activityTypesProm = EntityActivitiesRepository.instance.getTypes(
          0,
          100,
          entityId,
          CLINICAL_SUPPORT_SECTOR_ID,
          CLINICAL_SUPPORT__VITAL_SIGNS_ID,
          {signal: abort.current?.signal}
        );
        const measurementsProm = UsersRepository.instance.getAllUserHealthMeasurements(
          {
            userId: opts.userId,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            types: [
              // Get only the following types
              HealthMeasurementType.BloodPressure,
              HealthMeasurementType.BloodGlucose,
              HealthMeasurementType.BloodOxygen,
              HealthMeasurementType.BodyTemperature,
              HealthMeasurementType.BodyWeight,
              HealthMeasurementType.HeartRate,
            ],
          },
          {signal: abort.current?.signal}
        );

        const [activityTypesRes, measurements] = await Promise.all([
          activityTypesProm,
          measurementsProm,
        ]);

        // ------------------------------------------------------------
        // 1. Prepare the data by measure type and date range
        // ------------------------------------------------------------
        const arrayofDays = new Array(end.diff(start, 'days'))
          .fill(0)
          .map((_, i) => moment(start).add(i + 1, 'days'));

        const measuresByTypeMap = new Map<
          HealthMeasurementType,
          Map<string, HealthMeasurement[] | undefined>
        >();

        for (const measurement of measurements) {
          const measureDate = moment(measurement.endedAt);
          const measureType = measurement.type;

          if (!measuresByTypeMap.has(measureType)) {
            const initialDateMap = new Map<string, HealthMeasurement[] | undefined>();
            for (const day of arrayofDays) {
              initialDateMap.set(day.format('YYYY-MM-DD'), undefined);
            }

            measuresByTypeMap.set(measureType, initialDateMap);
          }

          const measureMap = measuresByTypeMap.get(measureType)!;
          const measureIsoDate = measureDate.format();
          let measureDateArray = measureMap.get(measureIsoDate);
          if (!measureDateArray) {
            measureDateArray = [measurement];
          } else {
            measureDateArray.unshift(measurement);
          }

          measureMap.set(measureIsoDate, measureDateArray);
          measuresByTypeMap.set(measureType, measureMap);
        }

        // ------------------------------------------------------------
        // 2. Transform the data to the chart format
        // ------------------------------------------------------------
        const result: HealthItemsMap = {};

        measuresByTypeMap.forEach((measureDateMap, measureType) => {
          const dates = new Set<string>();
          const item: HealthItem = {
            type: measureType,
            ...getTextsByMeasureType(activityTypesRes.data, measureType, t),
            values: measureType === HealthMeasurementType.BloodPressure ? [[], []] : [[]],
          };
          let minValue: number = Infinity;
          let maxValue: number = -Infinity;

          measureDateMap.forEach((measurements, isoDate) => {
            const date = moment(isoDate);
            dates.add(date.format('DD/MM'));

            measurements?.forEach((measurement) => {
              if (!measurement.data) {
                // Ignore measurements without data
                return;
              }

              const value = getValueByMeasureType(measureType, measurement);

              if (measureType === HealthMeasurementType.BloodPressure) {
                const val = value as [number, number];
                // calc min and max values
                const min = Math.min(val[0], val[1]);
                const max = Math.max(val[0], val[1]);
                minValue = Math.min(minValue, min);
                maxValue = Math.max(maxValue, max);
                const x = date.clone().set({hour: 0, minute: 0, second: 0, millisecond: 0}); // ignore hh:mm:ss in the x-axis
                const y0 = val[0];
                const y1 = val[1];
                // define label
                const label0 = [`${y0}`, date.format('DD MMM YY'), date.format('HH:mm')];
                const label1 = [`${y1}`, date.format('DD MMM YY'), date.format('HH:mm')];
                // add values
                item.values[0].push({x, y: y0, label: label0});
                item.values[1].push({x, y: y1, label: label1});
              } else {
                const val = value as number;
                // calc min and max values
                minValue = Math.min(minValue, val);
                maxValue = Math.max(maxValue, val);
                const x = date.clone().set({hour: 0, minute: 0, second: 0, millisecond: 0}); // ignore hh:mm:ss in the x-axis
                const y = val;
                // define label
                const label = [`${value}`, date.format('DD MMM YY'), date.format('HH:mm')];
                // add values
                item.values[0].push({x, y, label});
              }
            });
          });

          if (minValue !== undefined && maxValue !== undefined) {
            const minMaxRange = getMinMaxWithMarginByMeasureType(minValue, maxValue, measureType);
            item.domain = {y: minMaxRange};
          }
          result[measureType] = item;
        });

        // ------------------------------------------------------------
        // 3. Result
        // ------------------------------------------------------------
        setItems(Object.values(result));
        setFetching(false);
      } catch (error) {
        if (error.name === 'CanceledError') return;

        snackBar.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.health_charts.errors.fetch),
        });
        console.error(error);
        setFetching(false);
      }
    };

    // Reset state
    reset();
    // Fetch
    fetch(opts.startDate, opts.endDate);

    // Useffect Cleanup
    return () => {
      abort.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.userId, /*snackBar,*/ t, opts.startDate, opts.endDate]);

  const reset = () => {
    setFetching(true);
    setItems([]);
  };

  useEffect(() => {
    clearTimeout(timeout.current);
    abort.current?.abort();
    reset();
    timeout.current = setTimeout(() => refresh(), 500);

    return () => clearTimeout(timeout.current);
  }, [refresh]);

  return {
    items,
    fetching,
    refresh,
  };
}

// ----------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------
// ----------------------------------------------------------------------------------

type HealthItemsMap = {
  [key in HealthMeasurementType]?: HealthItem;
};

type HealthItem = {
  type: HealthMeasurementType;
  title?: string;
  subTitle?: [string, string?];
  values: DateChartValuesProp;
  domain?: DateChartDomainProp;
};

function getValueByMeasureType(
  measureType: HealthMeasurementType,
  measurement: HealthMeasurement
): number | [number, number] {
  switch (measureType) {
    case HealthMeasurementType.HeartRate:
      return measurement.data.beatsPerMinute;

    case HealthMeasurementType.BloodOxygen:
      return measurement.data.percentage;

    case HealthMeasurementType.BloodPressure:
      return [measurement.data.systolic, measurement.data.diastolic];

    case HealthMeasurementType.BloodGlucose:
      return measurement.data.value;

    case HealthMeasurementType.BodyTemperature:
      return measurement.data.temperatureInCelsius;

    case HealthMeasurementType.BodyWeight:
      return measurement.data.weightInKilograms;

    default:
      throw new Error('unkwown measure type');
  }
}

function getTextsByMeasureType(
  activityTypes: ActivityType[],
  measureType: HealthMeasurementType,
  t: TFunction
): {
  title: string;
  subTitle?: [string, string?];
} {
  const activityTypeName = activityTypes.find(
    (type) => type.healthMeasurementType === measureType
  )?.name;

  const title = activityTypeName ?? measureType;

  switch (measureType) {
    case HealthMeasurementType.HeartRate:
      return {
        title: title,
        subTitle: [t(translations.health_charts.heartRate.subtitle1)],
      };

    case HealthMeasurementType.BloodOxygen:
      return {
        title: title,
        subTitle: [
          t(translations.health_charts.bloodOxygen.subtitle1),
          t(translations.health_charts.bloodOxygen.subtitle2),
        ],
      };

    case HealthMeasurementType.BloodPressure:
      return {
        title: title,
        subTitle: [
          t(translations.health_charts.bloodPressure.subtitle1),
          t(translations.health_charts.bloodPressure.subtitle2),
        ],
      };

    case HealthMeasurementType.BloodGlucose:
      return {
        title: title,
        subTitle: [
          t(translations.health_charts.bloodGlucose.subtitle1),
          t(translations.health_charts.bloodGlucose.subtitle2),
        ],
      };

    case HealthMeasurementType.BodyTemperature:
      return {
        title: title,
        subTitle: [t(translations.health_charts.bodyTemperature.subtitle1)],
      };

    case HealthMeasurementType.BodyWeight:
      return {
        title: title,
      };

    default:
      throw new Error('unkwown measure type');
  }
}

function getMinMaxWithMarginByMeasureType(
  min: number,
  max: number,
  type: HealthMeasurementType
): [number, number] {
  const positiveNumber = ([min, max]: [number, number]): [number, number] => {
    return [min < 0 ? 0 : min, max < 0 ? 0 : max];
  };

  let result: [number, number];
  switch (type) {
    case HealthMeasurementType.HeartRate:
    case HealthMeasurementType.BloodOxygen:
    case HealthMeasurementType.BloodPressure:
    case HealthMeasurementType.BloodGlucose:
      result = [min - 10, max + 10];
      break;
    case HealthMeasurementType.BodyTemperature:
    case HealthMeasurementType.BodyWeight:
      result = [min - 1, max + 1];
      break;
    default:
      result = [min - 5, max + 5];
  }

  return positiveNumber(result);
}
